// NOTES
// resetOnUpdate is currently needed as an exception flag for Resource loading.
// Each Project page needs multiple Resource entities to be requested, for that reason these cannot just be replaced.
// This is the exception rather than the rule. All other entity types need replacement behaviour... currently.

import { get, union } from "lodash";

import { TIME_TO_STALE } from "../../../constants/api.js";
import { AUTHENTICATION } from "../authentication/authenticationConstants.js";
import { WATCHERS } from "../../actions/actionConstants.js";

export const INITIAL = {
  isReady: false,
  isFetching: false,
  isErrored: false,
  isStale: false,
  lastCreated: null, // using this for update dependency seems brittle.
  timeReceived: 0,
  page: null,
  totalPages: 0,
  data: [], // data is still used by projectWizard - look at how to refactor to stop this duplicated storage
  dataIndex: [],
  dataMap: null
};

export function entityReducer(actionEntity, resetOnUpdate) {
  if (!actionEntity) return null;

  return function (state = INITIAL, action = {}) {
    if (!action.type) return state;

    let dataMap;
    let dataIndex;

    const status = get(action, "payload.status", null);

    switch (action.type) {
      case actionEntity.CHECK_STALE:
        const isStale = Date.now() - state.timeReceived > TIME_TO_STALE;
        return {
          ...state,
          isStale
        };

      case actionEntity.PUSHER_UPDATE:
        const atId = get(action, `data.@id`, null);
        const currentData = get(state, `dataMap.${atId}`, {});

        // should we only allow pusher to update rather than create entities in the store
        // if (!currentData) return state;

        const entity = atId
          ? { [atId]: { ...currentData, ...action.data } }
          : undefined;

        return {
          ...state,
          dataMap: {
            ...state.dataMap,
            ...entity
          }
        };

      case actionEntity.RESET:
        return INITIAL;

      case actionEntity.GET:
      case actionEntity.POST:
      case actionEntity.PUT:
      case actionEntity.REQUEST:
      case actionEntity.DELETE:
        return {
          ...state,
          isFetching: true,
          isErrored: false
        };

      case actionEntity.RECEIVE:
        // if there is no hydra:view data we are dealing with a single page
        let page = 1;
        let totalPages = 1;

        // parse hydra:view data for pagination
        const view = get(action, "payload.hydra:view", null);
        const last = get(action, "payload.hydra:view.hydra:last", null);
        if (view && last) {
          const atIdStr = view["@id"];
          const pageURL = new URLSearchParams(
            atIdStr.substring(atIdStr.lastIndexOf("?") + 1)
          );

          const lastPageURL = new URLSearchParams(
            last.substring(last.lastIndexOf("?") + 1)
          );

          page = parseInt(pageURL.get("page"), 10);
          totalPages = parseInt(lastPageURL.get("page"), 10);
        }

        // use hydra:member array if available otherwise wrap single hydra:item in an array
        // so that the remainder of the processing code can use the same logic
        let data = action.payload["hydra:member"] || [action.payload];

        dataIndex = data.map(item => item["@id"]);

        dataMap = data.reduce((acc, item) => {
          var id = item["@id"];

          // @todo @id on Abacus endpoints are now (huge API platform upgrade in '25) different
          // for now we manipulate the ID in the SPA until we can address this more structurally
          if (id.endsWith("/measurements")) {
            id = id.replace(/\/measurements/g, "");
          }

          if (id.endsWith("/usage")) {
            id = id.replace(/\/usage/g, "");
          }

          if (id.endsWith("/charge")) {
            id = id.replace(/\/charge/g, "");
          }

          const currentData = get(state, `dataMap.${id}`, null);

          acc[id] = Object.assign({}, currentData, item);

          return acc;
        }, {});

        const timeReceived = Date.now();

        // create a union between new and old states if flagged.
        if (resetOnUpdate === false) {
          data = union(state.data, data);
          dataMap = Object.assign({}, state.dataMap, dataMap);
          dataIndex = union(state.dataIndex, dataIndex);
        }

        return {
          ...state,
          isReady: true,
          isStale: false,
          timeReceived,
          lastCreated: null,
          page,
          totalPages,
          data,
          dataIndex,
          dataMap
        };

      case actionEntity.DELETE_COMPLETE:
        const id = get(action, "payload.@id", null);

        // handle immediate delete returns
        if (status === 204 && id) {
          dataIndex = state.dataIndex.filter(item => item !== id);
          dataMap = { ...state.dataMap };
          delete dataMap[id];

          return {
            ...state,
            dataIndex,
            dataMap,
            isFetching: false
          };
        }

        return {
          ...state,
          isFetching: false
        };

      case actionEntity.POST_COMPLETE:
        const lastCreated = get(action, "payload.@id", null);
        return {
          ...state,
          isFetching: false,
          lastCreated
        };

      case actionEntity.GET_COMPLETE:
      case actionEntity.PUT_COMPLETE:
        return {
          ...state,
          isFetching: false
        };

      case actionEntity.FAILURE:
        // warn about a mallformed error and prevent SPA crashing
        if (!status) {
          console.warn(
            actionEntity.FAILURE,
            ":: No status found in action ::",
            action
          );
        }

        return {
          ...state,
          isFetching: false,
          isErrored: true // error now handled by error reducer?
        };

      case WATCHERS.RESET:
        return {
          ...state,
          isFetching: false
        };

      case AUTHENTICATION.LOGOUT.RECEIVE:
        // case AUTHENTICATION.LOGIN.FAILURE: // we may still need this ... ?
        return INITIAL;

      default:
        return state;
    }
  };
}

export default {};
